import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Table, Button, Space, Tag, Avatar, Input, Select, Modal, Form, Switch, DatePicker, InputNumber } from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { EditOutlined, UserOutlined, EyeOutlined, KeyOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import { apiService } from '../../../services/api';

const { Search } = Input;
const { Option } = Select;

// Backend'den gelen veri için genişletilmiş interface
interface AdminMentor {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  city?: string;
  country?: string;
  jobTitle?: string;
  company?: string;
  experienceYears?: number;
  hourlyRate?: number;
  bio?: string;
  isActive: boolean;
  isAvailable: boolean;
  subscriptionType: number; // Backend'den sayı olarak geliyor
  isSubscriptionActive: boolean;
  subscriptionEndDate?: string;
  createdAt: string;
  updatedAt?: string;
  dateOfBirth?: string;
  gender?: string;
  websiteUrl?: string;
  linkedInProfile?: string;
  gitHubProfile?: string; // Backend'de gitHubProfile olarak geliyor
  twitterProfile?: string;
  profileImage?: string;
}

const parseMentorsResponse = (response: any) => {
  const rawData = response?.data ?? response?.Data ?? response;
  const items = Array.isArray(rawData) ? rawData : rawData?.data ?? rawData?.Data ?? [];
  const total = rawData?.totalCount ?? rawData?.TotalCount ?? response?.totalCount ?? response?.TotalCount ?? 0;
  return { items: Array.isArray(items) ? items : [], total };
};

const MentorListTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [selectedMentor, setSelectedMentor] = useState<AdminMentor | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<string | undefined>(undefined);

  const { data, isLoading: loading } = useQuery({
    queryKey: adminQueryKeys.mentors(currentPage, pageSize, sortBy, sortOrder, searchText),
    queryFn: async () => {
      const r = await apiService.getAdminMentors(currentPage, pageSize, sortBy, sortOrder, searchText || undefined);
      const { items, total } = parseMentorsResponse(r);
      return { items: items as AdminMentor[], total };
    },
    staleTime: 1000 * 60 * 2,
  });
  const mentors = data?.items ?? [];
  const totalCount = data?.total ?? 0;

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) => apiService.updateAdminMentor(id, payload),
    onSuccess: (res) => {
      if (res.success) {
        setEditModalVisible(false);
        queryClient.invalidateQueries({ queryKey: ['admin-panel', 'mentors'] });
        toast.success('Mentor başarıyla güncellendi');
      } else toast.error(res.message || 'Mentor güncellenirken hata oluştu');
    },
    onError: () => toast.error('Mentor güncellenirken hata oluştu'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteAdminMentor(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Mentor başarıyla silindi');
        queryClient.invalidateQueries({ queryKey: ['admin-panel', 'mentors'] });
      } else toast.error(res.message || 'Mentor silinirken hata oluştu');
    },
    onError: () => toast.error('Mentor silinirken hata oluştu'),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, newPassword }: { id: number; newPassword: string }) => apiService.resetUserPassword(id, newPassword),
    onSuccess: (res) => {
      if (res.success) {
        setPasswordModalVisible(false);
        toast.success('Şifre başarıyla sıfırlandı');
      } else toast.error(res.message || 'Şifre sıfırlanırken hata oluştu');
    },
    onError: () => toast.error('Şifre sıfırlanırken hata oluştu'),
  });

  const handleEdit = (mentor: AdminMentor) => {
    setSelectedMentor(mentor);
    form.setFieldsValue({
      ...mentor,
      dateOfBirth: mentor.dateOfBirth ? dayjs(mentor.dateOfBirth) : null,
      subscriptionStartDate: (mentor as any).subscriptionStartDate ? dayjs((mentor as any).subscriptionStartDate) : null,
      subscriptionEndDate: mentor.subscriptionEndDate ? dayjs(mentor.subscriptionEndDate) : null,
      subscriptionType: typeof (mentor as any).subscriptionType === 'number' 
        ? ((mentor as any).subscriptionType === 0 ? 'Free' : (mentor as any).subscriptionType === 1 ? 'Monthly' : 'Yearly') 
        : (mentor as any).subscriptionType
    });
    setEditModalVisible(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Mentor Sil',
      content: 'Bu mentor\'u silmek istediğinizden emin misiniz?',
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handleUpdate = (values: any) => {
    if (!selectedMentor?.id) return;
    
    // SubscriptionType string'ini enum değerine çevir
    let subscriptionTypeValue: number | undefined;
    if (values.subscriptionType === 'Free') {
      subscriptionTypeValue = 0;
    } else if (values.subscriptionType === 'Monthly') {
      subscriptionTypeValue = 1;
    } else if (values.subscriptionType === 'Yearly') {
      subscriptionTypeValue = 2;
    } else if (values.subscriptionType === 'Premium') {
      // Premium için varsayılan olarak Monthly kullan
      subscriptionTypeValue = 1;
    }
    
    const payload = {
      ...values,
      isActive: values.isActive ?? selectedMentor.isActive,
      isAvailable: values.isAvailable ?? selectedMentor.isAvailable,
      subscriptionType: subscriptionTypeValue,
      subscriptionStartDate: values.subscriptionStartDate ? values.subscriptionStartDate.toISOString() : null,
      subscriptionEndDate: values.subscriptionEndDate ? values.subscriptionEndDate.toISOString() : null,
      dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null
    };
    updateMutation.mutate({ id: selectedMentor.id, payload });
  };

  const handleResetPassword = (mentor: AdminMentor) => {
    setSelectedMentor(mentor);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handlePasswordReset = (values: any) => {
    if (selectedMentor?.id) {
      resetPasswordMutation.mutate({ id: selectedMentor.id, newPassword: values.newPassword });
    }
  };

  // Search ve filter işlemleri artık backend'de yapılacak
  // Frontend'de sadece display için kullanılıyor
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1); // Arama yapıldığında ilk sayfaya git
    // Backend'e search parametresi gönderilecek
  };

  // Durum filtresi kaldırıldı

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) setPageSize(size);
  };

  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    if (sorter?.columnKey != null && sorter?.order != null) {
      const order = sorter.order === 'ascend' ? 'asc' : 'desc';
      const field = sorter.columnKey === 'name' ? 'name' : sorter.columnKey === 'email' ? 'email' : sorter.columnKey === 'hourlyRate' ? 'hourlyRate' : 'createdAt';
      setSortBy(field);
      setSortOrder(order);
      setCurrentPage(1);
    } else {
      setSortBy(undefined);
      setSortOrder(undefined);
    }
  };

  const columns = [
    {
      title: 'Profil',
      key: 'profile',
      width: 70,
      fixed: 'left' as const,
      render: (record: AdminMentor) => (
        <Avatar 
          size={36} 
          src={record.profileImage ? `data:image/jpeg;base64,${record.profileImage}` : undefined}
          icon={<UserOutlined />}
          style={{ backgroundColor: 'var(--success)' }}
        />
      )
    },
    {
      title: 'Ad Soyad',
      dataIndex: 'firstName',
      key: 'name',
      width: 150,
      fixed: 'left' as const,
      sorter: true,
      sortOrder: (sortBy === 'name' ? (sortOrder === 'asc' ? 'ascend' : sortOrder === 'desc' ? 'descend' : null) : null) as 'ascend' | 'descend' | null | undefined,
      render: (_: string, record: AdminMentor) => (
        <span style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</span>
      )
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      sorter: true,
      sortOrder: (sortBy === 'email' ? (sortOrder === 'asc' ? 'ascend' : sortOrder === 'desc' ? 'descend' : null) : null) as 'ascend' | 'descend' | null | undefined,
      ellipsis: true
    },
    {
      title: 'Pozisyon',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      width: 150,
      ellipsis: true
    },
    {
      title: 'Şirket',
      dataIndex: 'company',
      key: 'company',
      width: 150,
      ellipsis: true
    },
    {
      title: 'Deneyim',
      dataIndex: 'experienceYears',
      key: 'experienceYears',
      width: 100,
      render: (years: number) => years ? `${years} yıl` : '-'
    },
    {
      title: 'Saatlik Ücret',
      dataIndex: 'hourlyRate',
      key: 'hourlyRate',
      width: 120,
      sorter: true,
      sortOrder: (sortBy === 'hourlyRate' ? (sortOrder === 'asc' ? 'ascend' : sortOrder === 'desc' ? 'descend' : null) : null) as 'ascend' | 'descend' | null | undefined,
      render: (rate: number) => rate ? `$${rate}` : '-'
    },
    {
      title: 'Abonelik',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      width: 120,
      render: (value: any, record: AdminMentor) => {
        const label = typeof value === 'number' ? (value === 0 ? 'Ücretsiz' : value === 1 ? 'Aylık' : 'Yıllık') : (value === 'Free' ? 'Ücretsiz' : value === 'Premium' ? 'Premium' : 'Bilinmiyor');
        return (
          <Tag color={record.isSubscriptionActive ? 'green' : 'red'}>
            {label}
          </Tag>
        );
      }
    },
    {
      title: 'Abonelik Bitiş',
      dataIndex: 'subscriptionEndDate',
      key: 'subscriptionEndDate',
      width: 130,
      render: (_: any, record: AdminMentor) => {
        if (record.subscriptionType === 0) return '-';
        return record.subscriptionEndDate ? new Date(record.subscriptionEndDate).toLocaleDateString('tr-TR') : '-';
      }
    },
    {
      title: 'Kayıt Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      sorter: true,
      sortOrder: (sortBy === 'createdAt' ? (sortOrder === 'asc' ? 'ascend' : sortOrder === 'desc' ? 'descend' : null) : null) as 'ascend' | 'descend' | null | undefined,
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (record: AdminMentor) => (
        <Space size="small">
          <Button 
            type="text" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Düzenle"
            size="small"
          />
          <Button 
            type="text" 
            icon={<KeyOutlined />}
            onClick={() => handleResetPassword(record)}
            title="Şifre Sıfırla"
            style={{ color: 'var(--accent-500)' }}
            size="small"
          />
        </Space>
      )
    }
  ];

  return (
    <div className="panel-tab-content">
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, marginBottom: '16px', fontSize: '24px', fontWeight: 600 }}>Mentor Listesi</h2>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Search
            placeholder="Mentor ara..."
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            onSearch={(value) => handleSearch(value)}
            allowClear
            style={{ width: '100%', maxWidth: '400px' }}
            size="large"
          />
        </div>
      </div>

      <TabListLoading loading={loading}>
      <div className="panel-table-card">
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={mentors}
          rowKey="id"
          loading={loading}
          onChange={handleTableChange}
          size="middle"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} mentor`,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            pageSizeOptions: ['10', '20', '50', '100'],
            style: { marginTop: '16px' }
          }}
        />
      </div>
      </TabListLoading>

      <Modal
        title="Mentor Düzenle"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => form.submit()}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="firstName" label="Ad" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="lastName" label="Soyad" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </div>
          
          <Form.Item name="email" label="E-posta" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="phoneNumber" label="Telefon">
              <Input />
            </Form.Item>
            <Form.Item name="dateOfBirth" label="Doğum Tarihi">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="city" label="Şehir">
              <Input />
            </Form.Item>
            <Form.Item name="country" label="Ülke">
              <Input />
            </Form.Item>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="jobTitle" label="İş Unvanı">
              <Input />
            </Form.Item>
            <Form.Item name="company" label="Şirket">
              <Input />
            </Form.Item>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="experienceYears" label="Deneyim (Yıl)">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="hourlyRate" label="Saatlik Ücret">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="gender" label="Cinsiyet">
              <Select placeholder="Seçiniz">
                <Option value="Male">Erkek</Option>
                <Option value="Female">Kadın</Option>
                <Option value="Other">Diğer</Option>
              </Select>
            </Form.Item>
            <Form.Item name="websiteUrl" label="Website">
              <Input />
            </Form.Item>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="linkedInProfile" label="LinkedIn">
              <Input />
            </Form.Item>
            <Form.Item name="gitHubProfile" label="GitHub">
              <Input />
            </Form.Item>
          </div>
          
          <Form.Item name="twitterProfile" label="Twitter">
            <Input />
          </Form.Item>
          
          <Form.Item name="bio" label="Biyografi">
            <Input.TextArea rows={3} />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="subscriptionType" label="Abonelik Türü">
              <Select>
                <Option value="Free">Ücretsiz</Option>
                <Option value="Monthly">Aylık</Option>
                <Option value="Yearly">Yıllık</Option>
              </Select>
            </Form.Item>
            <Form.Item name="isSubscriptionActive" label="Abonelik Aktif" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          <Form.Item shouldUpdate noStyle>
            {() => {
              const subType = form.getFieldValue('subscriptionType');
              if (subType === 'Monthly' || subType === 'Yearly') {
                return (
                  <>
                    <Form.Item name="subscriptionStartDate" label="Abonelik Başlangıç Tarihi" rules={[{ required: false }]}>
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="subscriptionEndDate" label="Abonelik Bitiş Tarihi" rules={[{ required: false }]}>
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="isActive" label="Kullanıcı Aktif" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="isAvailable" label="Müsait" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Şifre Sıfırlama Modal */}
      <Modal
        title="Şifre Sıfırla"
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        onOk={() => passwordForm.submit()}
        width={400}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordReset}
        >
          <Form.Item
            name="newPassword"
            label="Yeni Şifre"
            rules={[
              { required: true, message: 'Yeni şifre gereklidir' },
              { min: 6, message: 'Şifre en az 6 karakter olmalıdır' }
            ]}
          >
            <Input.Password placeholder="Yeni şifreyi girin" />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            label="Şifre Onayı"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Şifre onayı gereklidir' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Şifreler eşleşmiyor'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Şifreyi tekrar girin" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MentorListTab;
